import { useState, useEffect, useRef, lazy, Suspense } from 'react';
import { Session } from '@supabase/supabase-js';
import { supabase, isConfigured, fetchClientIP, getAdminRole } from './services/supabase';
import { isWorldsHost } from './utils/worldsHost';
import { parseGuidedSessionId } from './services/classroomSync';
import { readPendingGiftCode } from './utils/pendingGiftCode';
import { UserProfile, AppState } from './types';
// Eager: tiny, on every screen / in the critical path.
import Login from './components/Login';
import Header from './components/Header';
import Footer from './components/Footer';
import { ReferralPromoPopup } from '@rehub/announcements';
import { Loader2, Play, GraduationCap, Users, QrCode, ClipboardCheck, BarChart3, ArrowRight, Check } from 'lucide-react';
import BrandedLoader from './components/BrandedLoader';

// ---------------------------------------------------------------------------
// Code-splitting: everything below loads as a separate chunk on demand.
//
// This is the single biggest performance lever for the app. Previously a
// visitor hitting a public /view or /embed tour downloaded the ENTIRE app
// (editor, admin panel, dashboard, three.js, etc.) before anything rendered.
// By lazy-loading per route/modal, the public viewer ships only the viewer +
// PSV + three chunks, and the marketing home page ships almost nothing extra.
// ---------------------------------------------------------------------------
/** Full-screen loader shown while a lazily-loaded route chunk downloads. */
const FullPageLoader = () => (
    <BrandedLoader label="Loading…" isWorldsTour={isWorldsHost()} />
);

/**
 * Wrap a dynamic import in React.lazy + a self-contained Suspense boundary so
 * the resulting component can be dropped into the existing JSX *unchanged*.
 * `fallback` is what shows while the chunk downloads:
 *   • full-page routes → <FullPageLoader />
 *   • modals / background decoration → null (no visible placeholder)
 */
function lazyWithSuspense<P extends object>(
    factory: () => Promise<{ default: React.ComponentType<P> }>,
    fallback: React.ReactNode,
): React.FC<P> {
    const Lazy = lazy(factory);
    return (props: P) => (
        <Suspense fallback={fallback}>
            <Lazy {...props} />
        </Suspense>
    );
}

const loader = <FullPageLoader />;

const PublicViewer = lazyWithSuspense(() => import('./components/PublicViewer'), loader);
// Meta Quest WebXR / PWA viewer. Kept fully standalone and lazy so the
// headset experience ships only the VR engine chunk (Three.js + WebXR) and
// none of the editor/admin/dashboard code.
const QuestApp = lazyWithSuspense(() => import('./components/quest/QuestApp'), loader);
const WorldsLibrary = lazyWithSuspense(() => import('./components/WorldsLibrary'), loader);
const WorldsGiftRedeem = lazyWithSuspense(() => import('./components/WorldsGiftRedeem'), loader);
const MobileCapturePage = lazyWithSuspense(() => import('./components/MobileCapturePage'), loader);
const ClientUpdatePortal = lazyWithSuspense(() => import('./components/ClientUpdatePortal'), loader);
const ShareDemoPage = lazyWithSuspense(() => import('../photoclear/components/ShareDemoPage'), loader);
const TourEditor = lazyWithSuspense(() => import('./components/TourEditor'), loader);
const Dashboard = lazyWithSuspense(() => import('./components/Dashboard'), loader);
const Settings = lazyWithSuspense(() => import('./components/Settings'), loader);
const Members = lazyWithSuspense(() => import('./components/Members'), loader);
const InviteAccept = lazyWithSuspense(() => import('./components/InviteAccept'), loader);
const TeacherDashboard = lazyWithSuspense(() => import('./components/education/TeacherDashboard'), loader);
const ClassJoin = lazyWithSuspense(() => import('./components/education/ClassJoin'), loader);
const AssessmentPlayer = lazyWithSuspense(() => import('./components/education/AssessmentPlayer'), loader);
const EducationInviteAccept = lazyWithSuspense(() => import('./components/education/EducationInviteAccept'), loader);
const RecommendedCameras = lazyWithSuspense(() => import('./components/RecommendedCameras'), loader);
const CaptureGuide = lazyWithSuspense(() => import('./components/CaptureGuide'), loader);
const VideoConverter = lazyWithSuspense(() => import('./components/VideoConverter'), loader);
// Modals + decorative background: no visible placeholder while loading.
const AdminPanel = lazyWithSuspense(() => import('./components/AdminPanel'), null);
const ReferralsPage = lazyWithSuspense(() => import('./components/ReferralsPage'), null);
const CreateTourModal = lazyWithSuspense(() => import('./components/CreateTourModal'), null);
const BuyCredits = lazyWithSuspense(() => import('./components/BuyCredits'), null);
const SubscriptionPlansModal = lazyWithSuspense(() => import('./components/SubscriptionPlansModal'), null);
const FluidBackground = lazyWithSuspense(() => import('./components/FluidBackground'), null);

const SSO_TARGET_URLS: Record<string, string> = {
    photoplan: 'https://photoplan.rehub.studio',
    photoclear: 'https://photoclear.rehub.studio',
    staging: 'https://staging.rehub.studio',
    sketchplan: 'https://sketchplan.rehub.studio',
    planos: 'https://planos.rehub.studio',
};

/**
 * A gift recipient who signed in via OAuth or a magic link comes back to
 * /auth/callback with their `?code=` gone, so send them to /redeem with the
 * stashed code instead of the dashboard. This has to be a full page load: the
 * /redeem route returns before this component's hooks, so it can never be
 * switched to in place.
 *
 * Returns true when it has taken over navigation.
 */
function returnToGiftRedeem(): boolean {
    const pending = readPendingGiftCode();
    if (!pending) return false;
    window.location.replace(`/redeem?code=${encodeURIComponent(pending)}`);
    return true;
}

/**
 * Normalize slug: mobile browsers/share targets may add a trailing slash or
 * extra path segments; use the first segment only.
 */
const normalizeSlug = (s: string) => s.replace(/\/+$/, '').trim().split('/')[0]?.trim() ?? '';

function App() {
    const [session, setSession] = useState<Session | null>(null);
    const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
    const [authLoading, setAuthLoading] = useState(true);
    // Tracks which user's profile is already loaded so that repeated auth
    // events for the SAME user (token auto-refresh, or a SIGNED_IN re-fired
    // when the browser tab regains focus) don't re-run fetchProfile and reset
    // view state (admin panel section, tour editor, etc.).
    const loadedProfileUserIdRef = useRef<string | null>(null);
    const [appState, setAppState] = useState<AppState>(AppState.INITIALIZING);
    const [showLoginModal, setShowLoginModal] = useState(false);
    const [loginMode, setLoginMode] = useState<'login' | 'signup'>('login');
    const [showCreateTourModal, setShowCreateTourModal] = useState(false);
    const [showBuyCredits, setShowBuyCredits] = useState(false);
    const [showSubscriptionPlansModal, setShowSubscriptionPlansModal] = useState(false);
    const [suggestedPlanTier, setSuggestedPlanTier] = useState<'pro' | 'agency' | 'business' | undefined>(undefined);
    const [currentPath, setCurrentPath] = useState(window.location.pathname);
    const [isAdmin, setIsAdmin] = useState(false);
    const [adminRole, setAdminRole] = useState<'admin' | 'superadmin' | 'editor' | null>(null);
    const [showAdminPanel, setShowAdminPanel] = useState(false);
    const [authCallbackError, setAuthCallbackError] = useState<string | null>(null);
    const [showReferrals, setShowReferrals] = useState(false);
    // Current tour being edited
    const [editingTourId, setEditingTourId] = useState<string | null>(null);

    // ------------------------------------------------------------------
    // NOTE: Standalone routes (/view, /embed, /worlds, /quest, /share, …) and
    // the 360Worlds consumer-host catch-all are handled by <RouteGate> above
    // this component — they render on their own and never mount <App/>. This
    // is deliberate: previously those route shortcuts `return`ed from inside
    // App *between* its useState and useEffect calls, so a re-render that
    // flipped branches ran a different number of hooks and crashed with React
    // error #300 ("rendered fewer hooks than expected"). Keeping App's hooks
    // unconditional fixes that. App only ever renders "app" routes below.
    // ------------------------------------------------------------------

    // Settings and Members (authenticated only)
    const isSettings = currentPath === '/settings';
    const isMembers = currentPath === '/members';
    const isEducation = currentPath === '/education' || currentPath === '/education/';
    const isEducationInvite = currentPath === '/education/invite';
    // Public resource pages
    const isRecommendedCameras = currentPath === '/recommended-360-cameras';
    const isCaptureGuide = currentPath === '/how-to-capture-a-property-for-a-virtual-tour';
    const isVideoConverter = currentPath === '/video-converter';

    // ------------------------------------------------------------------
    // Auth initialization (matching photoclear pattern)
    // ------------------------------------------------------------------
    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);
        const ssoTarget = urlParams.get('sso_target');
        const ssoTargetUrl = ssoTarget ? SSO_TARGET_URLS[ssoTarget] : null;

        const initApp = async () => {
            // Fetch client IP early for event logging
            fetchClientIP().catch(() => {});

            if (!isConfigured) {
                console.log('[Tours] Supabase not configured, skipping auth');
                setAuthLoading(false);
                setAppState(AppState.DASHBOARD);
                return;
            }

            const isAuthCallback = window.location.pathname === '/auth/callback';
            const authCode = urlParams.get('code');
            const redirectToSsoTarget = (s: Session) => {
                if (!ssoTargetUrl || !s.access_token || !s.refresh_token) return false;
                window.location.href = `${ssoTargetUrl}#sso_access_token=${s.access_token}&sso_refresh_token=${s.refresh_token}`;
                return true;
            };

            // Check for SSO tokens from cross-app navigation
            const hashParams = new URLSearchParams(window.location.hash.substring(1));
            const ssoAccessToken = hashParams.get('sso_access_token');
            const ssoRefreshToken = hashParams.get('sso_refresh_token');

            if (ssoAccessToken && ssoRefreshToken) {
                console.log('[Tours] SSO tokens detected, setting session...');
                window.history.replaceState({}, document.title, window.location.pathname);

                try {
                    const { data, error } = await supabase.auth.setSession({
                        access_token: ssoAccessToken,
                        refresh_token: ssoRefreshToken,
                    });

                    if (data.session) {
                        console.log('[Tours] SSO session established');
                        if (redirectToSsoTarget(data.session)) return;
                        setSession(data.session);
                        fetchProfile(data.session.user.id);
                        setAuthLoading(false);
                        setAppState(AppState.DASHBOARD);
                        return;
                    } else if (error) {
                        console.warn('[Tours] SSO session failed:', error.message);
                    }
                } catch (err) {
                    console.warn('[Tours] SSO session error:', err);
                }
            }

            // Explicitly exchange PKCE code on callback when present.
            if (isAuthCallback && authCode) {
                try {
                    const { data, error } = await supabase.auth.exchangeCodeForSession(authCode);
                    if (error) {
                        console.warn('[Tours] PKCE code exchange failed:', error.message);
                        setAuthCallbackError(error.message);
                        setAuthLoading(false);
                        return;
                    } else if (data.session) {
                        if (redirectToSsoTarget(data.session)) return;
                        if (returnToGiftRedeem()) return;
                        setSession(data.session);
                        window.history.replaceState({}, document.title, '/');
                        setCurrentPath('/');
                        fetchProfile(data.session.user.id);
                        return;
                    }
                } catch (err) {
                    console.warn('[Tours] PKCE exchange error:', err);
                    setAuthCallbackError(err instanceof Error ? err.message : 'Sign-in failed');
                    setAuthLoading(false);
                    return;
                }
            }

            // Auth callback timeout fallback
            if (isAuthCallback) {
                setTimeout(() => {
                    if (window.location.pathname === '/auth/callback') {
                        console.warn('[Tours] Auth callback timeout - redirecting');
                        window.history.replaceState({}, document.title, '/');
                        setCurrentPath('/');
                        setAuthLoading(false);
                        setAppState(AppState.DASHBOARD);
                    }
                }, 8000);
            }

            // Check for success/canceled query params from Stripe
            if (urlParams.get('success') === 'true') {
                // Payment successful — will show via updated credits
                window.history.replaceState({}, document.title, '/');
            }
            if (urlParams.get('canceled') === 'true') {
                window.history.replaceState({}, document.title, '/');
            }
            if (urlParams.get('studio') === 'success' || urlParams.get('studio') === 'canceled') {
                window.history.replaceState({}, document.title, '/');
            }

            // Timeout: if auth still initializing after 12s, show app (avoids stuck loading)
            const initTimeout = setTimeout(() => {
                setAuthLoading((prev) => {
                    if (prev) {
                        console.warn('[Tours] Auth init timeout — showing app');
                        return false;
                    }
                    return prev;
                });
                setAppState(AppState.DASHBOARD);
            }, 12000);

            // Fetch session
            supabase.auth.getSession().then(({ data: { session } }) => {
                clearTimeout(initTimeout);
                setSession(session);
                if (session) {
                    if (redirectToSsoTarget(session)) return;
                    if (isAuthCallback) {
                        if (returnToGiftRedeem()) return;
                        window.history.replaceState({}, document.title, '/');
                        setCurrentPath('/');
                    }
                    fetchProfile(session.user.id);
                } else if (!isAuthCallback) {
                    setAuthLoading(false);
                    setAppState(AppState.DASHBOARD);
                }
            }).catch(err => {
                clearTimeout(initTimeout);
                console.warn('[Tours] Session check failed:', err);
                if (!isAuthCallback) {
                    setAuthLoading(false);
                    setAppState(AppState.DASHBOARD);
                }
            });
        };

        initApp();

        // Auth state change listener
        const {
            data: { subscription },
        } = supabase.auth.onAuthStateChange(async (_event, session) => {
            console.log('[Tours] Auth state change:', _event, session ? 'has session' : 'no session');
            setSession(session);

            if (session) {
                if (ssoTargetUrl && session.access_token && session.refresh_token) {
                    window.location.href = `${ssoTargetUrl}#sso_access_token=${session.access_token}&sso_refresh_token=${session.refresh_token}`;
                    return;
                }

                // Return to a gated 360Worlds experience after signing in.
                const worldsReturn = new URLSearchParams(window.location.search).get('worlds_return');
                if (worldsReturn) {
                    window.location.href = worldsReturn;
                    return;
                }

                // If this is just a session refresh for the user we've already
                // loaded (token auto-refresh, or SIGNED_IN re-fired when the tab
                // regains focus), keep the live session fresh but DON'T re-run
                // fetchProfile — that would reset appState back to the dashboard
                // and remount the AdminPanel, losing the admin's current place.
                // Profile/credit changes still arrive via the realtime subscription.
                if (loadedProfileUserIdRef.current === session.user.id) {
                    return;
                }

                // Check for pending marketing consent
                const pendingConsent = localStorage.getItem('pending_marketing_consent');
                if (pendingConsent === 'true') {
                    localStorage.removeItem('pending_marketing_consent');
                    await supabase
                        .from('profiles')
                        .update({
                            marketing_consent: true,
                            marketing_consent_at: new Date().toISOString(),
                        })
                        .eq('id', session.user.id);
                } else if (pendingConsent === 'false') {
                    localStorage.removeItem('pending_marketing_consent');
                }

                setShowLoginModal(false);

                // Apply referral code if present. The same profiles.referral_code
                // powers both the credit "Refer & Earn" / Give 50 Get 50 program and the 360Worlds
                // peer referral (friend's first month free / referrer's free month),
                // so attribute both — each RPC is a one-time, self-referral-safe no-op
                // when it doesn't apply.
                const pendingRef = localStorage.getItem('referral_code');
                if (pendingRef) {
                    localStorage.removeItem('referral_code');
                    const ignore = () => {};
                    const provider = session?.user?.app_metadata?.provider;
                    const authMethod =
                      provider === 'google' ? 'google' : provider === 'email' ? 'magic_link' : null;
                    supabase.rpc('apply_referral_code', {
                      p_code: pendingRef,
                      p_auth_method: authMethod,
                      p_app_source: 'tours',
                    }).then(ignore, ignore);
                    supabase.rpc('apply_worlds_referral_code', { p_code: pendingRef }).then(ignore, ignore);
                    // A ?ref may also be a paid partner's affiliate code (separate
                    // table). Only the matching program applies; the others no-op.
                    supabase.rpc('apply_affiliate_referral', { p_code: pendingRef }).then(ignore, ignore);
                }

                const path = window.location.pathname;
                if (path === '/auth/callback' || path === '/login' || path === '/signup') {
                    if (returnToGiftRedeem()) return;
                    window.history.replaceState({}, document.title, '/dashboard');
                    setCurrentPath('/dashboard');
                }
                fetchProfile(session.user.id);
            } else {
                loadedProfileUserIdRef.current = null;
                setUserProfile(null);
                setIsAdmin(false);
                setAdminRole(null);
                setAuthLoading(false);
                setAppState(AppState.DASHBOARD);
            }
        });

        return () => subscription.unsubscribe();
    }, []);

    // Realtime profile subscription
    useEffect(() => {
        if (!session?.user) return;

        const channel = supabase
            .channel('tours-profile-updates')
            .on(
                'postgres_changes',
                {
                    event: 'UPDATE',
                    schema: 'public',
                    table: 'profiles',
                    filter: `id=eq.${session.user.id}`,
                },
                (payload) => {
                    if (payload.new) {
                        setUserProfile(payload.new as UserProfile);
                    }
                }
            )
            .subscribe();

        return () => {
            supabase.removeChannel(channel);
        };
        // Keyed on the user id (not the whole session object) so token refreshes
        // don't needlessly churn the realtime channel.
    }, [session?.user?.id]);

    // Visitor bounced here from a gated 360Worlds experience: prompt sign-in so
    // the post-login redirect (worlds_return) can send them back to the world.
    useEffect(() => {
        if (authLoading || session) return;
        const worldsReturn = new URLSearchParams(window.location.search).get('worlds_return');
        if (worldsReturn) {
            setLoginMode('login');
            setShowLoginModal(true);
        }
    }, [authLoading, session]);

    const fetchProfile = async (userId: string) => {
        // Mark this user as loaded up-front so concurrent/duplicate auth events
        // (getSession + INITIAL_SESSION, or a tab-focus refresh) are deduped.
        loadedProfileUserIdRef.current = userId;
        try {
            // Parallelize profile fetch and admin role check for faster login
            const [profileResult, role] = await Promise.all([
                supabase.from('profiles').select('*').eq('id', userId).single(),
                getAdminRole(userId)
            ]);

            if (profileResult.error) throw profileResult.error;
            
            // Set profile immediately so UI can render
            setUserProfile(profileResult.data);
            setAdminRole(role);
            setIsAdmin(!!role);
        } catch (error) {
            console.error('[Tours] Profile fetch error:', error);
            const currentUser = (await supabase.auth.getUser()).data.user;
            if (currentUser) {
                setUserProfile({
                    id: userId,
                    email: currentUser.email || '',
                    credits: 0,
                });
            }
        } finally {
            setAuthLoading(false);
            setAppState(AppState.DASHBOARD);
        }
    };

    const navigateTo = (path: string) => {
        setCurrentPath(path);
        window.history.pushState({}, '', path);
        // Notify <RouteGate> (and our own popstate listener) so a client-side
        // jump to a standalone route (e.g. onHome → /worlds on a worlds host)
        // swaps the tree instead of leaving <App/> mounted on a route it no
        // longer handles.
        window.dispatchEvent(new PopStateEvent('popstate'));
    };

    const handleEditTour = (tourId: string) => {
        setEditingTourId(tourId);
        setAppState(AppState.TOUR_OVERVIEW);
        // Give every project its own URL so it can be bookmarked, shared and
        // reopened on refresh (and opened directly from the admin panel).
        navigateTo(`/tour/${tourId}`);
    };

    const handleBackToDashboard = () => {
        setEditingTourId(null);
        setAppState(AppState.DASHBOARD);
        if (currentPath !== '/') navigateTo('/');
    };

    useEffect(() => {
        const onPopState = () => setCurrentPath(window.location.pathname);
        window.addEventListener('popstate', onPopState);
        return () => window.removeEventListener('popstate', onPopState);
    }, []);

    // ------------------------------------------------------------------
    // Deep-link project URLs: /tour/:tourId opens that project in the
    // editor. Keep the in-memory editor state in sync with the URL so
    // refreshes, shared links, browser back/forward and the admin panel's
    // "Open" links all resolve to the right project once auth is ready.
    // ------------------------------------------------------------------
    useEffect(() => {
        if (!session || !userProfile) return;
        const tourMatch = currentPath.match(/^\/tour\/([a-zA-Z0-9_-]+)$/);
        if (tourMatch) {
            setEditingTourId(tourMatch[1]);
            setAppState(AppState.TOUR_OVERVIEW);
        } else if (appState === AppState.TOUR_OVERVIEW) {
            setEditingTourId(null);
            setAppState(AppState.DASHBOARD);
        }
    }, [currentPath, session, userProfile, appState]);

    const handleOpenAdmin = () => navigateTo('/admin');
    const handleCloseAdmin = () => navigateTo('/');

    // Deep-link from emails: /?admin=... → normalize to the /admin route,
    // preserving the query so AdminPanel can still read ?admin=floorplans&order=…
    useEffect(() => {
        if (!session || !userProfile || !isAdmin) return;
        const params = new URLSearchParams(window.location.search);
        if (params.get('admin') && currentPath !== '/admin') {
            setCurrentPath('/admin');
            window.history.replaceState({}, '', '/admin' + window.location.search);
        }
    }, [session, userProfile, isAdmin, currentPath]);

    // Keep the admin console mounted whenever the URL is /admin so it is
    // bookmarkable and closes on browser back. Non-admins fall through to the
    // dashboard underneath.
    useEffect(() => {
        if (currentPath === '/admin') {
            if (isAdmin) setShowAdminPanel(true);
        } else {
            setShowAdminPanel(false);
        }
    }, [currentPath, isAdmin]);

    // Unsigned visits to the app root or /dashboard land on the focused login page.
    // An existing session on /login or /signup goes straight to the product.
    useEffect(() => {
        if (authLoading || isWorldsHost()) return;
        const path = window.location.pathname.replace(/\/+$/, '') || '/';
        const search = window.location.search || '';
        if (!session && (path === '/' || path === '/dashboard')) {
            window.history.replaceState({}, '', '/login' + search);
            setCurrentPath('/login');
        } else if (session && (path === '/login' || path === '/signup')) {
            window.history.replaceState({}, '', '/dashboard');
            setCurrentPath('/dashboard');
        }
    }, [authLoading, session]);

    const handleOpenCredits = () => {
        if (session && userProfile) {
            setShowBuyCredits(true);
        } else {
            setLoginMode('login');
            setShowLoginModal(true);
        }
    };

    const handleOpenPlans = () => {
        setSuggestedPlanTier(undefined);
        setShowSubscriptionPlansModal(true);
    };

    // ------------------------------------------------------------------
    // Loading state
    // ------------------------------------------------------------------
    if (authLoading && appState === AppState.INITIALIZING) {
        return (
            <div className="flex items-center justify-center min-h-screen bg-slate-950 flex-col gap-4">
                <Loader2 className="w-10 h-10 text-orange-500 animate-spin" />
                <p className="text-slate-400 animate-pulse">Loading...</p>
            </div>
        );
    }

    // ------------------------------------------------------------------
    // Auth callback error (e.g. PKCE code verifier not found)
    // ------------------------------------------------------------------
    const isPkceError = authCallbackError && (authCallbackError.includes('PKCE') || authCallbackError.includes('code verifier'));
    const friendlyPkceMessage = 'Sign-in couldn’t be completed because the verification step was lost. This usually happens if you opened the login link in a different browser or tab, or if site data was cleared. Please try again in the same browser without clearing data.';

    if (currentPath === '/auth/callback' && authCallbackError) {
        return (
            <div className="flex items-center justify-center min-h-screen bg-slate-950 flex-col gap-4 px-4">
                <p className="text-red-400 text-sm font-medium">Sign-in failed</p>
                <p className="text-slate-400 text-sm text-center max-w-md">
                    {isPkceError ? friendlyPkceMessage : authCallbackError}
                </p>
                <div className="flex flex-wrap items-center justify-center gap-3">
                    <a href="/" className="px-6 py-2 rounded-xl bg-orange-600 text-white text-sm font-semibold hover:bg-orange-500 transition-colors">Back to home</a>
                    {isPkceError && (
                        <a href="/" className="px-6 py-2 rounded-xl border border-slate-600 text-slate-300 text-sm font-medium hover:bg-slate-800 transition-colors">Try again</a>
                    )}
                </div>
            </div>
        );
    }

    // ------------------------------------------------------------------
    // Tour Editor (full-page)
    // ------------------------------------------------------------------
    if (appState === AppState.TOUR_OVERVIEW && editingTourId && session && userProfile) {
        return (
            <div className="min-h-screen bg-slate-950 text-slate-100 flex flex-col">
                <Header
                    user={userProfile}
                    onLogin={() => { setLoginMode('login'); setShowLoginModal(true); }}
                    onSignUp={() => { setLoginMode('signup'); setShowLoginModal(true); }}
                    onOpenCredits={handleOpenCredits}
                    onOpenPlans={handleOpenPlans}
                    onHome={handleBackToDashboard}
                    onNavigateToSettings={() => navigateTo('/settings')}
                    onNavigateToMembers={() => navigateTo('/members')}
                    isAdmin={isAdmin}
                    onOpenAdmin={handleOpenAdmin}
                />
                <div className="flex-1">
                    <TourEditor
                        tourId={editingTourId}
                        userId={userProfile.id}
                        userProfile={userProfile}
                        onBack={handleBackToDashboard}
                        onOpenCredits={handleOpenCredits}
                        onOpenPlans={(tier) => {
                            setSuggestedPlanTier(tier);
                            setShowSubscriptionPlansModal(true);
                        }}
                    />
                </div>

                {showBuyCredits && (
                    <BuyCredits user={userProfile} onClose={() => setShowBuyCredits(false)} onOpenPlans={() => { setShowBuyCredits(false); setShowSubscriptionPlansModal(true); }} />
                )}
                {showSubscriptionPlansModal && userProfile && (
                    <SubscriptionPlansModal
                        user={userProfile}
                        suggestedTier={suggestedPlanTier}
                        onClose={() => { setShowSubscriptionPlansModal(false); setSuggestedPlanTier(undefined); }}
                    />
                )}
                {showAdminPanel && adminRole && (
                    <AdminPanel user={userProfile} adminRole={adminRole} onClose={handleCloseAdmin} />
                )}
            </div>
        );
    }

    if (session && userProfile && isSettings) {
        return (
            <div className="min-h-screen bg-slate-950 text-slate-100 flex flex-col">
                <Header
                    user={userProfile}
                    onLogin={() => {}}
                    onSignUp={() => {}}
                    onOpenCredits={handleOpenCredits}
                    onOpenPlans={handleOpenPlans}
                    onHome={() => navigateTo('/')}
                    onNavigateToSettings={() => navigateTo('/settings')}
                    onNavigateToMembers={() => navigateTo('/members')}
                    isAdmin={isAdmin}
                    onOpenAdmin={handleOpenAdmin}
                />
                <Settings userProfile={userProfile} onBack={() => navigateTo('/')} />
                {showSubscriptionPlansModal && userProfile && (
                    <SubscriptionPlansModal user={userProfile} onClose={() => { setShowSubscriptionPlansModal(false); setSuggestedPlanTier(undefined); }} />
                )}
                {showAdminPanel && adminRole && (
                    <AdminPanel user={userProfile} adminRole={adminRole} onClose={handleCloseAdmin} />
                )}
            </div>
        );
    }
    if (session && userProfile && isMembers) {
        return (
            <div className="min-h-screen bg-slate-950 text-slate-100 flex flex-col">
                <Header
                    user={userProfile}
                    onLogin={() => {}}
                    onSignUp={() => {}}
                    onOpenCredits={handleOpenCredits}
                    onOpenPlans={handleOpenPlans}
                    onHome={() => navigateTo('/')}
                    onNavigateToSettings={() => navigateTo('/settings')}
                    onNavigateToMembers={() => navigateTo('/members')}
                    isAdmin={isAdmin}
                    onOpenAdmin={handleOpenAdmin}
                />
                <Members userProfile={userProfile} onBack={() => navigateTo('/')} />
                {showSubscriptionPlansModal && userProfile && (
                    <SubscriptionPlansModal user={userProfile} onClose={() => { setShowSubscriptionPlansModal(false); setSuggestedPlanTier(undefined); }} />
                )}
                {showAdminPanel && adminRole && (
                    <AdminPanel user={userProfile} adminRole={adminRole} onClose={handleCloseAdmin} />
                )}
            </div>
        );
    }

    if (session && userProfile && isEducation) {
        return (
            <div className="min-h-screen bg-slate-950 text-slate-100 flex flex-col">
                <Header
                    user={userProfile}
                    onLogin={() => {}}
                    onSignUp={() => {}}
                    onOpenCredits={handleOpenCredits}
                    onOpenPlans={handleOpenPlans}
                    onHome={() => navigateTo(isWorldsHost() ? '/worlds' : '/')}
                    onNavigateToSettings={() => navigateTo('/settings')}
                    onNavigateToMembers={() => navigateTo('/members')}
                    onNavigateToEducation={() => navigateTo('/education')}
                    isAdmin={isAdmin}
                    onOpenAdmin={handleOpenAdmin}
                />
                <TeacherDashboard userProfile={userProfile} onBack={() => navigateTo(isWorldsHost() ? '/worlds' : '/')} />
                {showAdminPanel && adminRole && (
                    <AdminPanel user={userProfile} adminRole={adminRole} onClose={handleCloseAdmin} />
                )}
            </div>
        );
    }

    if (isEducation && !session) {
        const educationSteps = [
            { icon: Users, title: 'Create your classes', body: 'Set up a classroom or named school, then add classes like “Year 5B”. Each class gets its own join code.' },
            { icon: QrCode, title: 'Share a code, link or QR', body: 'Pupils join in seconds with a QR code, a share link with the code prefilled, or the short class code — no pupil emails or accounts.' },
            { icon: ClipboardCheck, title: 'Set quizzes & worksheets', body: 'Build curriculum-linked quizzes, publish them to a class and attach PDF worksheets to any world.' },
            { icon: BarChart3, title: 'Track results', body: 'See who has joined and how learners scored, all from one dashboard.' },
        ];
        return (
            <div className="min-h-screen bg-[#030712] text-slate-100 flex flex-col relative overflow-hidden">
                <FluidBackground />
                <Header
                    user={null}
                    onLogin={() => { setLoginMode('login'); setShowLoginModal(true); }}
                    onSignUp={() => { setLoginMode('signup'); setShowLoginModal(true); }}
                    onOpenCredits={handleOpenCredits}
                    onHome={() => navigateTo(isWorldsHost() ? '/worlds' : '/')}
                />
                <main className="flex-1 relative z-10 px-4 py-12 sm:py-16">
                    <div className="mx-auto max-w-5xl">
                        {/* Hero */}
                        <div className="text-center max-w-2xl mx-auto">
                            <span className="inline-flex items-center gap-2 rounded-full border border-cyan-400/40 bg-cyan-500/10 px-4 py-1.5 text-xs font-semibold text-cyan-200">
                                <GraduationCap size={14} /> For teachers &amp; schools
                            </span>
                            <h1 className="mt-5 text-3xl sm:text-4xl font-extrabold tracking-tight">Teacher dashboard</h1>
                            <p className="mt-3 text-slate-400 sm:text-lg">
                                Run immersive 360Worlds lessons for your class — create classes, invite colleagues, set quizzes and worksheets, and track learner results in one place.
                            </p>
                            <div className="mt-7 flex flex-wrap items-center justify-center gap-3">
                                <button
                                    onClick={() => { setLoginMode('login'); setShowLoginModal(true); }}
                                    className="inline-flex items-center gap-2 rounded-lg bg-cyan-500 hover:bg-cyan-400 text-slate-950 font-semibold px-5 py-2.5 transition-colors"
                                >
                                    Sign in <ArrowRight size={16} />
                                </button>
                                <button
                                    onClick={() => { setLoginMode('signup'); setShowLoginModal(true); }}
                                    className="rounded-lg border border-slate-700 hover:border-slate-500 px-5 py-2.5 font-semibold text-slate-200 transition-colors"
                                >
                                    Create an account
                                </button>
                            </div>
                            <p className="mt-3 text-xs text-slate-500">
                                Sign in with the account that holds your Solo Teacher or School licence.
                            </p>
                        </div>

                        {/* How it works */}
                        <div className="mt-14 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                            {educationSteps.map((step, i) => (
                                <div key={step.title} className="rounded-2xl border border-slate-800 bg-slate-900/40 p-5">
                                    <div className="flex items-center gap-2 text-cyan-300">
                                        <span className="inline-flex h-9 w-9 items-center justify-center rounded-xl border border-cyan-500/30 bg-cyan-500/10">
                                            <step.icon size={18} />
                                        </span>
                                        <span className="text-xs font-mono text-slate-500">0{i + 1}</span>
                                    </div>
                                    <h3 className="mt-4 text-sm font-semibold text-white">{step.title}</h3>
                                    <p className="mt-1.5 text-xs leading-relaxed text-slate-400">{step.body}</p>
                                </div>
                            ))}
                        </div>

                        {/* Plan distinction */}
                        <div className="mt-6 grid gap-4 sm:grid-cols-2">
                            <div className="rounded-2xl border border-slate-800 bg-slate-900/40 p-6">
                                <h3 className="text-sm font-semibold text-white flex items-center gap-2">
                                    <GraduationCap size={16} className="text-cyan-400" /> Solo Teacher
                                </h3>
                                <p className="mt-2 text-xs leading-relaxed text-slate-400">
                                    One educator and up to 30 learners. Create classes, share join codes and set quizzes — ideal for a single classroom or online tutoring.
                                </p>
                            </div>
                            <div className="rounded-2xl border border-slate-800 bg-slate-900/40 p-6">
                                <h3 className="text-sm font-semibold text-white flex items-center gap-2">
                                    <Users size={16} className="text-cyan-400" /> School
                                </h3>
                                <p className="mt-2 text-xs leading-relaxed text-slate-400">
                                    One named school per licence, a central admin view and colleague invites. Three sizes: up to 15 teachers and 500 pupils, 50 and 1,500, or 100 and 3,000. Larger schools and trusts — contact us for a custom quote.
                                </p>
                            </div>
                        </div>

                        {/* Footer links */}
                        <div className="mt-10 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-sm">
                            <a href="https://360worlds.com/getting-started" className="inline-flex items-center gap-1.5 text-slate-400 hover:text-cyan-300 transition-colors">
                                <Check size={14} /> Setup guide
                            </a>
                            <a href="https://360worlds.com/education" className="inline-flex items-center gap-1.5 text-slate-400 hover:text-cyan-300 transition-colors">
                                <Check size={14} /> About school plans
                            </a>
                            <a href="https://360worlds.com/pricing" className="inline-flex items-center gap-1.5 text-slate-400 hover:text-cyan-300 transition-colors">
                                <Check size={14} /> See pricing
                            </a>
                            <button
                                onClick={() => navigateTo('/worlds')}
                                className="inline-flex items-center gap-1.5 text-slate-400 hover:text-cyan-300 transition-colors"
                            >
                                <Play size={14} /> Explore worlds
                            </button>
                        </div>
                    </div>
                </main>
                {showLoginModal && (
                    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4 overflow-y-auto">
                        <div className="max-w-md w-full my-auto py-8">
                            <Login onClose={() => setShowLoginModal(false)} initialMode={loginMode} app={isWorldsHost() ? 'worlds' : 'tours'} />
                        </div>
                    </div>
                )}
            </div>
        );
    }

    // 360 Video Converter (studio tool — requires sign-in so videos can be saved into a tour)
    if (isVideoConverter) {
        if (session && userProfile) {
            return (
                <div className="min-h-screen bg-[#030712] text-slate-100 flex flex-col">
                    <Header
                        user={userProfile}
                        onLogin={() => {}}
                        onSignUp={() => {}}
                        onOpenCredits={handleOpenCredits}
                        onOpenPlans={handleOpenPlans}
                        onHome={() => navigateTo('/')}
                        onNavigateToSettings={() => navigateTo('/settings')}
                        onNavigateToMembers={() => navigateTo('/members')}
                        isAdmin={isAdmin}
                        onOpenAdmin={handleOpenAdmin}
                    />
                    <VideoConverter
                        user={userProfile}
                        onBack={() => navigateTo('/')}
                        onUseInTour={(tourId) => handleEditTour(tourId)}
                    />
                    {showBuyCredits && userProfile && (
                        <BuyCredits user={userProfile} onClose={() => setShowBuyCredits(false)} onOpenPlans={() => { setShowBuyCredits(false); setShowSubscriptionPlansModal(true); }} />
                    )}
                    {showSubscriptionPlansModal && userProfile && (
                        <SubscriptionPlansModal user={userProfile} onClose={() => { setShowSubscriptionPlansModal(false); setSuggestedPlanTier(undefined); }} />
                    )}
                    {showAdminPanel && adminRole && (
                        <AdminPanel user={userProfile} adminRole={adminRole} onClose={handleCloseAdmin} />
                    )}
                </div>
            );
        }
        // Not signed in: fall through to the home/dashboard page (with its
        // login CTAs). After signing in, this block renders the converter.
    }

    // Recommended 360 cameras resource page (public — works signed in or out)
    if (isRecommendedCameras) {
        return (
            <div className="min-h-screen bg-[#030712] text-slate-100 flex flex-col">
                <Header
                    user={session ? userProfile : null}
                    onLogin={() => { setLoginMode('login'); setShowLoginModal(true); }}
                    onSignUp={() => { setLoginMode('signup'); setShowLoginModal(true); }}
                    onOpenCredits={handleOpenCredits}
                    onOpenPlans={userProfile ? handleOpenPlans : undefined}
                    onHome={() => navigateTo('/')}
                    onNavigateToSettings={session ? () => navigateTo('/settings') : undefined}
                    onNavigateToMembers={session ? () => navigateTo('/members') : undefined}
                    isAdmin={isAdmin}
                    onOpenAdmin={handleOpenAdmin}
                />
                <RecommendedCameras
                    onBack={() => navigateTo('/')}
                    onCreateTour={() => {
                        if (session && userProfile) {
                            navigateTo('/');
                            setShowCreateTourModal(true);
                        } else {
                            setLoginMode('signup');
                            setShowLoginModal(true);
                        }
                    }}
                />
                <Footer />
                {showLoginModal && (
                    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4 overflow-y-auto">
                        <div className="max-w-md w-full my-auto py-8">
                            <Login onClose={() => setShowLoginModal(false)} initialMode={loginMode} app={isWorldsHost() ? 'worlds' : 'tours'} />
                        </div>
                    </div>
                )}
                {showAdminPanel && adminRole && userProfile && (
                    <AdminPanel user={userProfile} adminRole={adminRole} onClose={handleCloseAdmin} />
                )}
            </div>
        );
    }

    // Property capture guide resource page (public — works signed in or out)
    if (isCaptureGuide) {
        return (
            <div className="min-h-screen bg-[#030712] text-slate-100 flex flex-col">
                <Header
                    user={session ? userProfile : null}
                    onLogin={() => { setLoginMode('login'); setShowLoginModal(true); }}
                    onSignUp={() => { setLoginMode('signup'); setShowLoginModal(true); }}
                    onOpenCredits={handleOpenCredits}
                    onOpenPlans={userProfile ? handleOpenPlans : undefined}
                    onHome={() => navigateTo('/')}
                    onNavigateToSettings={session ? () => navigateTo('/settings') : undefined}
                    onNavigateToMembers={session ? () => navigateTo('/members') : undefined}
                    isAdmin={isAdmin}
                    onOpenAdmin={handleOpenAdmin}
                />
                <CaptureGuide
                    onBack={() => navigateTo('/')}
                    onCreateTour={() => {
                        if (session && userProfile) {
                            navigateTo('/');
                            setShowCreateTourModal(true);
                        } else {
                            setLoginMode('signup');
                            setShowLoginModal(true);
                        }
                    }}
                />
                <Footer />
                {showLoginModal && (
                    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4 overflow-y-auto">
                        <div className="max-w-md w-full my-auto py-8">
                            <Login onClose={() => setShowLoginModal(false)} initialMode={loginMode} app={isWorldsHost() ? 'worlds' : 'tours'} />
                        </div>
                    </div>
                )}
                {showAdminPanel && adminRole && userProfile && (
                    <AdminPanel user={userProfile} adminRole={adminRole} onClose={handleCloseAdmin} />
                )}
            </div>
        );
    }

    // Invite accept page (token in query string)
    if (currentPath === '/invite/accept') {
        const inviteToken = new URLSearchParams(typeof window !== 'undefined' ? window.location.search : '').get('token');
        return (
            <div className="min-h-screen bg-[#030712] text-slate-100 flex flex-col relative overflow-hidden">
                {!session && <FluidBackground />}
                <Header
                    user={session ? userProfile : null}
                    onLogin={() => { setLoginMode('login'); setShowLoginModal(true); }}
                    onSignUp={() => { setLoginMode('signup'); setShowLoginModal(true); }}
                    onOpenCredits={handleOpenCredits}
                    onOpenPlans={userProfile ? handleOpenPlans : undefined}
                    onHome={() => navigateTo('/')}
                />
                <main className="flex-1 relative z-10">
                    <InviteAccept
                        token={inviteToken}
                        user={userProfile ?? null}
                        onLoginClick={() => { setLoginMode('login'); setShowLoginModal(true); }}
                        onSignUpClick={() => { setLoginMode('signup'); setShowLoginModal(true); }}
                        onAccepted={() => navigateTo('/')}
                    />
                </main>
                {showLoginModal && (
                    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4 overflow-y-auto">
                        <div className="max-w-md w-full my-auto py-8">
                            <Login onClose={() => setShowLoginModal(false)} initialMode={loginMode} app={isWorldsHost() ? 'worlds' : 'tours'} />
                        </div>
                    </div>
                )}
            </div>
        );
    }

    if (isEducationInvite) {
        const inviteToken = new URLSearchParams(typeof window !== 'undefined' ? window.location.search : '').get('token');
        return (
            <div className="min-h-screen bg-[#030712] text-slate-100 flex flex-col relative overflow-hidden">
                {!session && <FluidBackground />}
                <Header
                    user={session ? userProfile : null}
                    onLogin={() => { setLoginMode('login'); setShowLoginModal(true); }}
                    onSignUp={() => { setLoginMode('signup'); setShowLoginModal(true); }}
                    onOpenCredits={handleOpenCredits}
                    onOpenPlans={userProfile ? handleOpenPlans : undefined}
                    onHome={() => navigateTo(isWorldsHost() ? '/worlds' : '/')}
                />
                <main className="flex-1 relative z-10">
                    <EducationInviteAccept
                        token={inviteToken}
                        user={userProfile ?? null}
                        onLoginClick={() => { setLoginMode('login'); setShowLoginModal(true); }}
                        onSignUpClick={() => { setLoginMode('signup'); setShowLoginModal(true); }}
                        onAccepted={() => navigateTo('/education')}
                    />
                </main>
                {showLoginModal && (
                    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4 overflow-y-auto">
                        <div className="max-w-md w-full my-auto py-8">
                            <Login onClose={() => setShowLoginModal(false)} initialMode={loginMode} app={isWorldsHost() ? 'worlds' : 'tours'} />
                        </div>
                    </div>
                )}
            </div>
        );
    }

    // Unauthenticated product entry: focused sign-in / sign-up, not a marketing homepage.
    // Public resource pages return above this point.
    if (!session && appState === AppState.DASHBOARD && !isWorldsHost()) {
        const barePath = currentPath.replace(/\/+$/, '') || '/';
        const entryMode = barePath === '/signup' ? 'signup' : 'login';
        return (
            <Login
                app="tours"
                initialMode={entryMode}
                onModeChange={(next) => {
                    const nextPath = next === 'signup' ? '/signup' : '/login';
                    const search = window.location.search || '';
                    if ((window.location.pathname.replace(/\/+$/, '') || '/') !== nextPath) {
                        window.history.replaceState({}, '', nextPath + search);
                        setCurrentPath(nextPath);
                    }
                }}
            />
        );
    }

    // ------------------------------------------------------------------
    // Main App (Dashboard)
    // ------------------------------------------------------------------
    return (
        <div className={`min-h-screen bg-[#030712] text-slate-100 flex flex-col relative ${appState === AppState.DASHBOARD && !session ? 'overflow-y-auto' : 'overflow-hidden'}`}>
            <Header
                user={session ? userProfile : null}
                onLogin={() => { setLoginMode('login'); setShowLoginModal(true); }}
                onSignUp={() => { setLoginMode('signup'); setShowLoginModal(true); }}
                onOpenCredits={handleOpenCredits}
                onOpenPlans={userProfile ? handleOpenPlans : undefined}
                onHome={handleBackToDashboard}
                onNavigateToSettings={() => navigateTo('/settings')}
                onNavigateToMembers={() => navigateTo('/members')}
                onNavigateToEducation={() => navigateTo('/education')}
                onNavigateToReferrals={() => setShowReferrals(true)}
                isAdmin={isAdmin}
                onOpenAdmin={handleOpenAdmin}
            />

            {/* Main Content */}
            <main className={`relative z-10 ${session && userProfile ? 'flex-1' : ''}`}>
                {appState === AppState.DASHBOARD && session && !userProfile && (
                    <div className="flex flex-1 items-center justify-center py-24">
                        <div className="flex flex-col items-center gap-4">
                            <Loader2 className="w-10 h-10 text-orange-500 animate-spin" />
                            <p className="text-slate-400">Loading your account...</p>
                        </div>
                    </div>
                )}
                {appState === AppState.DASHBOARD && session && userProfile && !isSettings && !isMembers && (
                    <Dashboard
                        userProfile={userProfile}
                        onCreateTour={() => setShowCreateTourModal(true)}
                        onEditTour={handleEditTour}
                        onOpenCredits={handleOpenCredits}
                        onOpenPlans={handleOpenPlans}
                    />
                )}

            </main>

            <Footer />

            {/* Modals */}
            {showLoginModal && (
                <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4 overflow-y-auto">
                    <div className="max-w-md w-full my-auto py-8">
                        <Login onClose={() => setShowLoginModal(false)} initialMode={loginMode} app={isWorldsHost() ? 'worlds' : 'tours'} />
                    </div>
                </div>
            )}
            {showCreateTourModal && userProfile && (
                <CreateTourModal
                    userId={userProfile.id}
                    creditsBalance={userProfile.credits}
                    onClose={() => setShowCreateTourModal(false)}
                    onTourCreated={(tourId) => {
                        setShowCreateTourModal(false);
                        handleEditTour(tourId);
                    }}
                />
            )}

            {showBuyCredits && userProfile && (
                <BuyCredits user={userProfile} onClose={() => setShowBuyCredits(false)} onOpenPlans={() => { setShowBuyCredits(false); setShowSubscriptionPlansModal(true); }} />
            )}

            {showSubscriptionPlansModal && userProfile && (
                <SubscriptionPlansModal
                    user={userProfile}
                    suggestedTier={suggestedPlanTier}
                    onClose={() => { setShowSubscriptionPlansModal(false); setSuggestedPlanTier(undefined); }}
                />
            )}

            {showAdminPanel && userProfile && isAdmin && adminRole && (
                <AdminPanel
                    user={userProfile}
                    adminRole={adminRole}
                    onClose={handleCloseAdmin}
                />
            )}

            {showReferrals && userProfile && (
                <ReferralsPage userId={userProfile.id} onClose={() => setShowReferrals(false)} />
            )}

            <ReferralPromoPopup
                client={supabase}
                userId={userProfile?.id}
                appSource="tours"
                onInvite={() => setShowReferrals(true)}
                suppressed={showReferrals || showAdminPanel}
            />
        </div>
    );
}

/**
 * Top-level router.
 *
 * Standalone routes (public viewer, embed, worlds library, gift redeem, class
 * join, assessment player, Quest/VR, mobile capture, client-update portal) and
 * the 360Worlds consumer-host catch-all render on their own here and never
 * mount the full <App/>. This keeps the heavy agent dashboard/auth machinery
 * off those pages AND — critically — guarantees <App/> only mounts for "app"
 * routes, so its hook order stays unconditional. These shortcuts used to live
 * *inside* App between its useState and useEffect calls, which crashed with
 * React error #300 ("rendered fewer hooks than expected") whenever a re-render
 * (e.g. after SIGNED_IN) flipped between an early return and the full render.
 *
 * <App/>'s navigateTo dispatches a `popstate` event after pushState so a
 * client-side jump into a standalone route re-runs this dispatch.
 */
function RouteGate() {
    const [path, setPath] = useState(window.location.pathname);

    useEffect(() => {
        const onNav = () => setPath(window.location.pathname);
        window.addEventListener('popstate', onNav);
        return () => window.removeEventListener('popstate', onNav);
    }, []);

    const shareMatch = path.match(/^\/share\/([a-zA-Z0-9_-]+)$/);
    if (shareMatch) return <ShareDemoPage slug={shareMatch[1]} />;

    if (path === '/worlds' || path === '/worlds/') return <WorldsLibrary />;
    if (path === '/redeem' || path === '/redeem/') return <WorldsGiftRedeem />;
    if (path === '/class/join' || path === '/class/join/') return <ClassJoin />;
    const joinMatch = path.match(/^\/join(?:\/([A-Za-z0-9-]+))?\/?$/);
    if (joinMatch) return <ClassJoin initialCode={joinMatch[1] ?? null} />;

    const assessmentMatch = path.match(/^\/assessment\/([a-zA-Z0-9_-]+)$/);
    if (assessmentMatch) {
        const params = new URLSearchParams(typeof window !== 'undefined' ? window.location.search : '');
        return (
            <AssessmentPlayer
                assessmentId={assessmentMatch[1]}
                assignmentId={params.get('assignment')}
            />
        );
    }

    const viewMatch = path.match(/^\/view\/(.+)$/);
    if (viewMatch) return <PublicViewer slug={normalizeSlug(viewMatch[1])} />;

    const embedMatch = path.match(/^\/embed\/(.+)$/);
    if (embedMatch) return <PublicViewer slug={normalizeSlug(embedMatch[1])} isEmbed />;

    const captureMatch = path.match(/^\/capture\/(.+)$/);
    // Public phone capture page for the Recorded Guide handoff.
    if (captureMatch) return <MobileCapturePage token={normalizeSlug(captureMatch[1])} />;

    const clientUpdateMatch = path.match(/^\/client-update\/(.+)$/);
    // Tokenised client update portal (venue/hospitality managed-service).
    if (clientUpdateMatch) return <ClientUpdatePortal token={normalizeSlug(clientUpdateMatch[1])} />;

    // Meta Quest WebXR viewer: /quest, /quest/:code and /vr-app. Standalone
    // (no header/footer/auth) so it can be packaged + submitted as a Meta
    // Horizon Store PWA.
    const questMatch = path.match(/^\/(?:quest|vr-app)(?:\/(.+))?$/);
    if (questMatch) {
        return (
            <QuestApp
                initialCode={questMatch[1] ? normalizeSlug(questMatch[1]) : null}
                guidedSessionId={parseGuidedSessionId(window.location.search)}
            />
        );
    }

    // Dedicated VR subdomain (e.g. vr.360tours.studio): the whole host is the
    // headset experience, so the root path (or /:code) boots straight into
    // the Quest launcher instead of the marketing homepage.
    if (/^vr\./i.test(window.location.hostname)) {
        const vrCodeMatch = path.match(/^\/([A-Za-z0-9_-]+)\/?$/);
        return (
            <QuestApp
                initialCode={vrCodeMatch ? normalizeSlug(vrCodeMatch[1]) : null}
                guidedSessionId={parseGuidedSessionId(window.location.search)}
            />
        );
    }

    // Consumer 360Worlds host (e.g. explore.360worlds.com): only the worlds
    // library + viewer (+ education pupil/teacher routes) are exposed. Any
    // other path on this host boots the library so consumers never reach the
    // 360Tours agent dashboard/editor/admin.
    if (
        isWorldsHost()
        && path !== '/education'
        && path !== '/education/'
        && path !== '/education/invite'
    ) {
        return <WorldsLibrary />;
    }

    return <App />;
}

export default RouteGate;
